import { useState, useEffect, useMemo, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil, Plus, AlertCircle, BarChart4 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { useData } from "@/hooks/useData";
import { useWaterSources } from "@/hooks/useWaterSources";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { formatNumber } from "@/utils/numberFormatter";
import { WaterModalForm } from "./WaterModalForm";
import { WaterEntry, WaterPayload, PollutantDocWithId } from "@/types/water";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  calculateWaterEntryHydricFootprint,
  calculateWaterEntryOutputFootprint,
  calculateDischargeGreyWaterFootprint,
  calculateSludgeGreyWaterFootprint,
  calculateWaterEntryBlueFootprint,
  calculateWaterEntryGreyFootprint,
} from "../../utils/calculations/annualConsumptions";
import { useDischargeTypes } from "@/hooks/useDischargeTypes";
import {
  calculateTotalWaterHydricFootprint,
  calculateTotalWaterOutputFootprint,
  calculateTotalDischargeWaterFootprint,
  calculateTotalSludgeWaterFootprint,
  calculateTotalGrayWaterFootprint,
  calculateTotalWaterFootprint,
} from "../../utils/calculations/annualConsumptions";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useIPMAStation } from "@/hooks/useIPMAStation";

// Empty entry for new entries
const emptyWaterEntry: WaterEntry = {
  waterSurfaceArea: "",
  waterSurfaceAreaUnit: "m²",
  waterOrigins: [],
  discharge: {
    hasDischarge: null,
    dischargeEntries: [],
    pollutants: [],
    ac_descargas_polu: 0,
  },
  sludge: {
    hasSludge: null,
    sludgeQuantity: "",
    sludgeUnit: "t",
    moistureContent: "",
    isDepositedInBasin: null,
    pollutants: [],
    ac_lamas_polu: 0,
  },
  total_ph_agua: 0,
  ph_agua_saida: 0,
  ph_agua_azul: 0,
  ph_agua_cinzenta: 0,
  ph_agua_total: 0,
};

interface WaterStepProps {
  existingData?: WaterPayload;
  onDataChange: (payload: WaterPayload) => void;
  showTitle?: boolean;
  activityType?: "extraction" | "transformation";
}

export const WaterStep = ({
  existingData,
  onDataChange,
  showTitle = true,
  activityType,
}: WaterStepProps) => {
  const { t } = useLanguage();

  // Get company data to access weather station reference
  const { company } = useCurrentUserCompany();
  
  // Get IPMA station data for evaporation and precipitation rates
  // For mixed companies, get rates dynamically based on activity type
  const { evaporationRate, precipitationRate, getRatesForActivity } = useIPMAStation();

  // Initialize state from existingData
  const [waterEntries, setWaterEntries] = useState<WaterEntry[]>(
    Array.isArray(existingData?.waterEntries) ? existingData.waterEntries : []
  );

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<WaterEntry>(emptyWaterEntry);

  // Fetch data
  const { waterSources } = useWaterSources();
  const { dischargeTypes } = useDischargeTypes();

  const { data: pollutants } = useData<PollutantDocWithId>({
    collectionName: "setup_pollutants",
  });

  // Sync with existingData changes
  useEffect(() => {
    if (existingData) {
      const newEntries = Array.isArray(existingData.waterEntries)
        ? existingData.waterEntries
        : [];
      
      // Only update if the data is actually different to prevent unnecessary re-renders
      if (JSON.stringify(newEntries) !== JSON.stringify(waterEntries)) {
        setWaterEntries(newEntries);
      }
    }
  }, [existingData, waterEntries]);

  // Memoize the getPollutantDoc function
  const getPollutantDoc = useCallback(
    (pollutantName: string) => {
      return (pollutants || []).find((doc) => doc.name === pollutantName);
    },
    [pollutants]
  );

  // Helper function to create payload from entries
  const createPayload = useCallback(
    (entries: WaterEntry[]) => {
      const entriesCopy = JSON.parse(JSON.stringify(entries));
      const calculatedEntries = entriesCopy.map((entry: WaterEntry) => {
        try {
          // CRITICAL FIX: Check if entry already has valid footprint values
          // Only recalculate if values are missing (undefined, null, or all zeros)
          const hasExistingFootprints = 
            (entry.total_ph_agua !== undefined && entry.total_ph_agua !== null && entry.total_ph_agua > 0) ||
            (entry.ph_agua_total !== undefined && entry.ph_agua_total !== null && entry.ph_agua_total > 0) ||
            (entry.ph_agua_azul !== undefined && entry.ph_agua_azul !== null && entry.ph_agua_azul > 0) ||
            (entry.ph_agua_cinzenta !== undefined && entry.ph_agua_cinzenta !== null && entry.ph_agua_cinzenta > 0);
          
          // If entry has valid footprints, preserve them and only recalculate if needed for display
          // This prevents overwriting valid values on initial load after login
          if (hasExistingFootprints) {
            // Preserve existing values - don't recalculate
            return {
              ...entry,
              total_ph_agua: entry.total_ph_agua || 0,
              ph_agua_saida: entry.ph_agua_saida || 0,
              ph_agua_azul: entry.ph_agua_azul || 0,
              ph_agua_cinzenta: entry.ph_agua_cinzenta || 0,
              ph_agua_total: entry.ph_agua_total || (entry.ph_agua_azul || 0) + (entry.ph_agua_cinzenta || 0),
              discharge: { 
                ...entry.discharge, 
                ac_descargas_polu: entry.discharge?.ac_descargas_polu || 0 
              },
              sludge: { 
                ...entry.sludge, 
                ac_lamas_polu: entry.sludge?.ac_lamas_polu || 0 
              },
            };
          }

          // Only recalculate if entry doesn't have valid footprints yet (new entries or entries with missing values)
          // Calculate new values, but preserve existing values if recalculation produces 0
          // This prevents corruption when recalculation fails or returns 0 due to missing data
          const calculated_total_ph_agua = calculateWaterEntryHydricFootprint(entry) || 0;
          const total_ph_agua = (calculated_total_ph_agua === 0 && entry.total_ph_agua > 0)
            ? entry.total_ph_agua
            : calculated_total_ph_agua;

          // Calculate discharge grey water footprint (ac_descargas)
          const calculated_ac_descargas_polu = calculateDischargeGreyWaterFootprint(
            entry.discharge,
            getPollutantDoc
          ) || 0;
          const ac_descargas_polu = (calculated_ac_descargas_polu === 0 && entry.discharge?.ac_descargas_polu > 0)
            ? entry.discharge.ac_descargas_polu
            : calculated_ac_descargas_polu;
          
          const calculated_ph_agua_saida = calculateWaterEntryOutputFootprint(entry) || 0;
          const ph_agua_saida = (calculated_ph_agua_saida === 0 && entry.ph_agua_saida > 0)
            ? entry.ph_agua_saida
            : calculated_ph_agua_saida;

          // Calculate sludge grey water footprint (ac_lamas) - only if sludge stays in company
          const calculated_ac_lamas_polu = calculateSludgeGreyWaterFootprint(
            entry.sludge,
            getPollutantDoc
          ) || 0;
          const existing_ac_lamas_polu = entry.sludge?.ac_lamas_polu || 0;
          const ac_lamas_polu = (calculated_ac_lamas_polu === 0 && existing_ac_lamas_polu > 0)
            ? existing_ac_lamas_polu
            : calculated_ac_lamas_polu;

          // Calculate total grey water using the corrected main formula
          const calculated_ph_agua_cinzenta = calculateWaterEntryGreyFootprint(entry, getPollutantDoc) || 0;
          const ph_agua_cinzenta = (calculated_ph_agua_cinzenta === 0 && entry.ph_agua_cinzenta > 0)
            ? entry.ph_agua_cinzenta
            : calculated_ph_agua_cinzenta;

          // Calculate blue water footprint
          // For mixed companies, use the activity type
          let entryEvaporationRate = evaporationRate;
          let entryPrecipitationRate = precipitationRate;
          
          if (company?.activityType === "extraction_and_transformation" && activityType) {
            const rates = getRatesForActivity(activityType);
            entryEvaporationRate = rates.evaporationRate;
            entryPrecipitationRate = rates.precipitationRate;
          }

          // Formula: ph_azul = Σ q_agua + sludge_component - Σ q_descargas + [area × (te - tp)]
          // Where sludge_component = (q_lamas × h_lamas ÷ 100) if sludge is not deposited in basin
          const calculated_ph_agua_azul = calculateWaterEntryBlueFootprint(
            entry,
            entryEvaporationRate,
            entryPrecipitationRate
          ) || 0;
          const ph_agua_azul = (calculated_ph_agua_azul === 0 && entry.ph_agua_azul > 0)
            ? entry.ph_agua_azul
            : calculated_ph_agua_azul;

          const ph_agua_total = (ph_agua_azul || 0) + (ph_agua_cinzenta || 0);

          return {
            ...entry,
            total_ph_agua,
            ph_agua_saida,
            ph_agua_azul,
            ph_agua_cinzenta,
            ph_agua_total,
            discharge: { ...entry.discharge, ac_descargas_polu },
            sludge: { ...entry.sludge, ac_lamas_polu },
          };
        } catch (error) {
          console.error("Error calculating water entry:", error);
          // Preserve existing values instead of setting to 0 on error
          return {
            ...entry,
            total_ph_agua: entry.total_ph_agua || 0,
            ph_agua_saida: entry.ph_agua_saida || 0,
            ph_agua_azul: entry.ph_agua_azul || 0,
            ph_agua_cinzenta: entry.ph_agua_cinzenta || 0,
            ph_agua_total: entry.ph_agua_total || 0,
            discharge: { 
              ...entry.discharge, 
              ac_descargas_polu: entry.discharge?.ac_descargas_polu || 0 
            },
            sludge: { 
              ...entry.sludge, 
              ac_lamas_polu: entry.sludge?.ac_lamas_polu || 0 
            },
          };
        }
      });

      // Calculate aggregated values
      const total_ph_agua_used =
        calculateTotalWaterHydricFootprint(calculatedEntries);
      const total_ph_agua_saida =
        calculateTotalWaterOutputFootprint(calculatedEntries);
      const total_ph_agua_azul = calculatedEntries.reduce(
        (sum: any, entry: any) => sum + (entry.ph_agua_azul || 0),
        0
      );
      const total_ac_descargas =
        calculateTotalDischargeWaterFootprint(calculatedEntries);
      const total_ac_lamas =
        calculateTotalSludgeWaterFootprint(calculatedEntries);
      const total_ph_agua_cinzenta =
        calculateTotalGrayWaterFootprint(calculatedEntries);
      const ph_agua = calculateTotalWaterFootprint(calculatedEntries);

      return {
        waterEntries: calculatedEntries,
        total_ph_agua_used,
        total_ph_agua_saida,
        total_ph_agua_azul,
        total_ac_descargas,
        total_ac_lamas,
        total_ph_agua_cinzenta,
        ph_agua,
      };
    },
    [getPollutantDoc, evaporationRate, precipitationRate, company?.activityType, getRatesForActivity]
  );

  // Recalculate footprints only when pollutants are loaded and entries have changed
  useEffect(() => {
    if (pollutants && pollutants.length > 0 && waterEntries.length > 0) {
      // Only recalculate if entries have data but no calculated values
      const needsRecalculation = waterEntries.some(entry => {
        const hasData = entry.waterOrigins?.length > 0 || entry.discharge?.hasDischarge || entry.sludge?.hasSludge;
        const hasNoCalculatedValues = (
          entry.ph_agua_total === 0 && 
          entry.ph_agua_azul === 0 && 
          entry.ph_agua_cinzenta === 0 &&
          entry.total_ph_agua === 0 &&
          entry.ph_agua_saida === 0
        );
        return hasData && hasNoCalculatedValues;
      });
      
      if (needsRecalculation) {
        const updatedEntries = waterEntries.map(entry => {
          const hasData = entry.waterOrigins?.length > 0 || entry.discharge?.hasDischarge || entry.sludge?.hasSludge;
          const hasNoCalculatedValues = (
            entry.ph_agua_total === 0 && 
            entry.ph_agua_azul === 0 && 
            entry.ph_agua_cinzenta === 0 &&
            entry.total_ph_agua === 0 &&
            entry.ph_agua_saida === 0
          );
          
          if (hasData && hasNoCalculatedValues) {
            try {
              const total_ph_agua = calculateWaterEntryHydricFootprint(entry) || 0;
              const ac_descargas_polu = calculateDischargeGreyWaterFootprint(entry.discharge, getPollutantDoc) || 0;
              const ph_agua_saida = calculateWaterEntryOutputFootprint(entry) || 0;
              const ac_lamas_polu = entry.sludge?.hasSludge && entry.sludge?.isDepositedInBasin
                ? calculateSludgeGreyWaterFootprint(entry.sludge, getPollutantDoc) || 0
                : 0;
              const ph_agua_cinzenta = calculateWaterEntryGreyFootprint(entry, getPollutantDoc) || 0;
              
              let entryEvaporationRate = evaporationRate;
              let entryPrecipitationRate = precipitationRate;
              
              if (company?.activityType === "extraction_and_transformation" && activityType) {
                const rates = getRatesForActivity(activityType);
                entryEvaporationRate = rates.evaporationRate;
                entryPrecipitationRate = rates.precipitationRate;
              }
              
              const ph_agua_azul = calculateWaterEntryBlueFootprint(
                entry,
                entryEvaporationRate,
                entryPrecipitationRate
              ) || 0;
              
              const ph_agua_total = (ph_agua_azul || 0) + (ph_agua_cinzenta || 0);

              return {
                ...entry,
                total_ph_agua,
                ph_agua_saida,
                ph_agua_azul,
                ph_agua_cinzenta,
                ph_agua_total,
                discharge: { ...entry.discharge, ac_descargas_polu },
                sludge: { ...entry.sludge, ac_lamas_polu },
              };
            } catch (error) {
              console.error("Error recalculating water entry:", error);
              return entry;
            }
          }
          return entry;
        });
        
        setWaterEntries(updatedEntries);
      }
    }
  }, [pollutants, getPollutantDoc, evaporationRate, precipitationRate, company?.activityType, getRatesForActivity, activityType]);

  // Calculate entries for display (using current waterEntries)
  const calculatedEntries = useMemo(() => {
    return createPayload(waterEntries).waterEntries;
  }, [waterEntries, createPayload]);

  // No automatic onDataChange calls - only during user actions

  // Entry handlers
  const openModal = (entry?: WaterEntry, idx?: number) => {
    if (entry && typeof idx === "number") {
      setFormEntry({ ...entry });
      setEditingIndex(idx);
    } else {
      setFormEntry(emptyWaterEntry);
      setEditingIndex(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setFormEntry(emptyWaterEntry);
      setEditingIndex(null);
    }, 100);
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      closeModal();
    } else {
      setModalOpen(true);
    }
  };

  const handleSave = () => {
    const newEntries = [...waterEntries];
    if (editingIndex !== null) {
      const existingEntry = waterEntries[editingIndex];
      const updatedEntry = {
        ...formEntry,
        // Preserve calculated values if they exist and formEntry doesn't have them
        total_ph_agua: formEntry.total_ph_agua || existingEntry.total_ph_agua || 0,
        ph_agua_saida: formEntry.ph_agua_saida || existingEntry.ph_agua_saida || 0,
        ph_agua_azul: formEntry.ph_agua_azul || existingEntry.ph_agua_azul || 0,
        ph_agua_cinzenta: formEntry.ph_agua_cinzenta || existingEntry.ph_agua_cinzenta || 0,
        ph_agua_total: formEntry.ph_agua_total || existingEntry.ph_agua_total || 0,
        discharge: {
          ...formEntry.discharge,
          ac_descargas_polu: formEntry.discharge.ac_descargas_polu || existingEntry.discharge.ac_descargas_polu || 0,
        },
        sludge: {
          ...formEntry.sludge,
          ac_lamas_polu: formEntry.sludge.ac_lamas_polu || existingEntry.sludge.ac_lamas_polu || 0,
        },
      };
      newEntries[editingIndex] = updatedEntry;
    } else {
      newEntries.push(formEntry);
    }

    setWaterEntries(newEntries);

    // Update parent with new data
    const payload = createPayload(newEntries);
    onDataChange(payload);

    closeModal();
  };

  const handleRemoveEntry = (index: number) => {
    const newEntries = waterEntries.filter((_, i) => i !== index);
    setWaterEntries(newEntries);

    // Update parent with new data
    const payload = createPayload(newEntries);
    onDataChange(payload);
  };

  return (
    <div className="space-y-6">
      {/* Title + Add button */}
      {waterSources.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700 text-sm mb-4">
          {t("water.noWaterSourcesAvailable")}
        </div>
      )}
      {showTitle ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t("footprints.categories.water.title")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("footprints.guidelines.water")}
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    onClick={() => openModal()}
                    variant="outline"
                    className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:hover:text-gray-500"
                    disabled={
                      waterEntries.length >= 1 || waterSources.length === 0
                    }
                  >
                    <Plus className="h-4 w-4" />
                    {t("water.addWaterEntry")}
                  </Button>
                </div>
              </TooltipTrigger>
              {waterEntries.length >= 1 && (
                <TooltipContent>
                  <p>{t("water.onlyOneEntryAllowed")}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <div className="flex justify-end mb-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    onClick={() => openModal()}
                    variant="outline"
                    className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300 disabled:hover:text-gray-500"
                    disabled={
                      waterEntries.length >= 1 || waterSources.length === 0
                    }
                  >
                    <Plus className="h-4 w-4" />
                    {t("water.addWaterEntry")}
                  </Button>
                </div>
              </TooltipTrigger>
              {waterEntries.length >= 1 && (
                <TooltipContent>
                  <p>{t("water.onlyOneEntryAllowed")}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {/* Water Entries */}
      {!calculatedEntries || calculatedEntries.length === 0 ? (
        <Alert variant="default" className="bg-gray-50 border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
            <AlertDescription className="text-gray-500">
              {t("water.noWaterEntries")}
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <div className="space-y-4">
            {calculatedEntries.map((entry: any, idx: any) => (
              <Card
                key={idx}
                className="shadow-sm hover:shadow transition-shadow"
              >
                <div className="flex justify-end items-center p-2 border-b border-gray-100 bg-gray-50">
                  <div className="flex space-x-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openModal(waterEntries[idx], idx)}
                    >
                      <Pencil className="h-4 w-4 text-gray-400 hover:text-teal-600" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveEntry(idx)}
                    >
                      <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                    </Button>
                  </div>
                </div>
                <CardContent className="py-4">
                  <div className="space-y-3">
                    {/* Surface Area */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        {t("water.surfaceArea")}:
                      </span>
                      <Badge
                        variant="secondary"
                        className="bg-gray-50 text-gray-700"
                      >
                        {formatNumber(Number(entry.waterSurfaceArea || 0))}{" "}
                        {entry.waterSurfaceAreaUnit}
                      </Badge>
                    </div>

                    {/* Water Origins */}
                    {entry.waterOrigins && entry.waterOrigins.length > 0 && (
                      <>
                        <hr className="border-gray-200" />
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-700">
                            {t("water.waterOrigins")}:
                          </span>
                          {entry.waterOrigins.map((origin: any, originIdx: any) => (
                            <div
                              key={originIdx}
                              className="flex justify-between items-center text-sm pl-4"
                            >
                              <span className="text-gray-600">
                                {origin.sourceName}
                              </span>
                              <Badge
                                variant="secondary"
                                className="bg-gray-50 text-gray-700"
                              >
                                {formatNumber(Number(origin.quantity || 0))}{" "}
                                {origin.unit}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {/* Discharge */}
                    {entry.discharge?.hasDischarge &&
                      entry.discharge?.dischargeEntries &&
                      entry.discharge.dischargeEntries.length > 0 && (
                        <>
                          <hr className="border-gray-200" />
                          <div className="space-y-2">
                            <span className="text-sm font-medium text-gray-700">
                              {t("water.discharge")}:
                            </span>
                            {entry.discharge.dischargeEntries.map(
                              (discharge: any, dischargeIdx: any) => (
                                <div
                                  key={dischargeIdx}
                                  className="flex justify-between items-center text-sm pl-4"
                                >
                                  <span className="text-gray-600">
                                    {discharge.dischargeTypeName}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="bg-gray-50 text-gray-700"
                                  >
                                    {formatNumber(
                                      Number(discharge.quantity || 0)
                                    )}{" "}
                                    {discharge.unit}
                                  </Badge>
                                </div>
                              )
                            )}

                            {/* Pollutants */}
                            {entry.discharge.pollutants &&
                              entry.discharge.pollutants.length > 0 && (
                                <div className="pl-4 space-y-1">
                                  <span className="text-xs font-medium text-gray-600">
                                    {t("water.pollutants")}:
                                  </span>
                                  {entry.discharge.pollutants.map(
                                    (pollutant: any, pollIdx: any) => (
                                      <div
                                        key={pollIdx}
                                        className="flex justify-between items-center text-xs pl-2"
                                      >
                                        <span className="text-gray-500">
                                          {pollutant.pollutantName}
                                        </span>
                                        <div className="flex items-center gap-1">
                                          <span className="text-gray-500">
                                            {formatNumber(
                                              Number(
                                                pollutant.concentration || 0
                                              )
                                            )}{" "}
                                            mg/L
                                          </span>
                                          {pollutant.hasMonitoring && (
                                            <span className="text-xs text-green-600">
                                              ({t("water.monitored")}:{" "}
                                              {formatNumber(
                                                Number(
                                                  pollutant.concentrationInReceiver ||
                                                    0
                                                )
                                              )}
                                              )
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )
                                  )}

                                  {/* Gray Water from Discharge Pollutants */}
                                  <div className="mt-2 pt-2 border-t border-teal-200 bg-teal-50 rounded-md p-2">
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-teal-900 font-medium">
                                        {t("water.grayWaterFromPollutants")}
                                      </span>
                                      <Badge
                                        variant="secondary"
                                        className="bg-teal-100 text-teal-900"
                                      >
                                        {formatNumber(
                                          entry.discharge?.ac_descargas_polu ||
                                            0
                                        )}{" "}
                                        m³
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        </>
                      )}

                    {/* Sludge */}
                    {entry.sludge?.hasSludge && (
                      <>
                        <hr className="border-gray-200" />
                        <div className="space-y-2">
                          <span className="text-sm font-medium text-gray-700">
                            {t("water.sludge")}:
                          </span>
                          <div className="space-y-1 pl-4">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">
                                {t("water.quantity")}:
                              </span>
                              <Badge
                                variant="secondary"
                                className="bg-gray-50 text-gray-700"
                              >
                                {formatNumber(
                                  Number(entry.sludge.sludgeQuantity || 0)
                                )}{" "}
                                {entry.sludge.sludgeUnit}
                              </Badge>
                            </div>
                            {entry.sludge.moistureContent && (
                              <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-600">
                                  {t("water.moistureContentShort")}:
                                </span>
                                <Badge
                                  variant="secondary"
                                  className="bg-gray-50 text-gray-700"
                                >
                                  {formatNumber(
                                    Number(entry.sludge.moistureContent || 0)
                                  )}
                                  %
                                </Badge>
                              </div>
                            )}
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">
                                {t("water.depositedInBasin")}:
                              </span>
                              <Badge
                                variant="secondary"
                                className={`${
                                  entry.sludge.isDepositedInBasin
                                    ? "bg-green-50 text-green-700"
                                    : "bg-orange-50 text-orange-700"
                                }`}
                              >
                                {entry.sludge.isDepositedInBasin
                                  ? t("water.yes")
                                  : t("water.no")}
                              </Badge>
                            </div>
                          </div>

                          {/* Sludge Pollutants */}
                          {entry.sludge.isDepositedInBasin &&
                            entry.sludge.pollutants &&
                            entry.sludge.pollutants.length > 0 && (
                              <div className="pl-4 space-y-1">
                                <span className="text-xs font-medium text-gray-600">
                                  {t("water.sludgePollutants")}:
                                </span>
                                {entry.sludge.pollutants.map(
                                  (pollutant: any, pollIdx: any) => (
                                    <div
                                      key={pollIdx}
                                      className="flex justify-between items-center text-xs pl-2"
                                    >
                                      <span className="text-gray-500">
                                        {pollutant.pollutantName}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <span className="text-gray-500">
                                          {formatNumber(
                                            Number(pollutant.concentration || 0)
                                          )}{" "}
                                          mg/L
                                        </span>
                                        {pollutant.hasMonitoring && (
                                          <span className="text-xs text-green-600">
                                            ({t("water.monitored")}:{" "}
                                            {formatNumber(
                                              Number(
                                                pollutant.concentrationInReceiver ||
                                                  0
                                              )
                                            )}
                                            )
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}

                                {/* Gray Water from Sludge Pollutants */}
                                <div className="mt-2 pt-2 border-t border-teal-200 bg-teal-50 rounded-md p-2">
                                  <div className="flex justify-between items-center text-xs">
                                    <span className="text-teal-900 font-medium">
                                      {t("water.grayWaterFromSludgePollutants")}
                                      :
                                    </span>
                                    <Badge
                                      variant="secondary"
                                      className="bg-teal-100 text-teal-900"
                                    >
                                      {formatNumber(
                                        entry.sludge?.ac_lamas_polu || 0
                                      )}{" "}
                                      m³
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main Footprint Cards */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Blue Water */}
            <div className="rounded-lg border border-blue-100 bg-white p-6">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">
                  {t("water.blueWater")}
                </span>
              </div>

              <p className="text-xs italic text-blue-600 ml-7 mb-4">
                {t("water.blueWaterDescription")}
              </p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-blue-700">
                  {formatNumber(
                    calculatedEntries.reduce(
                      (sum: any, entry: any) => sum + (entry.ph_agua_azul || 0),
                      0
                    )
                  )}{" "}
                  <span>
                    m<sup>3</sup>
                  </span>
                </span>
              </div>
            </div>

            {/* Gray Water */}
            <div className="rounded-lg border border-gray-100 bg-white p-6">
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">
                  {t("water.grayWater")}
                </span>
              </div>

              <p className="text-xs italic text-gray-600 ml-7 mb-4">
                {t("water.grayWaterDescription")}
              </p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-gray-700">
                  {formatNumber(
                    calculatedEntries.reduce(
                      (sum: any, entry: any) => sum + (entry.ph_agua_cinzenta || 0),
                      0
                    )
                  )}{" "}
                  <span>
                    m<sup>3</sup>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={handleModalOpenChange}>
        <WaterModalForm
          key={`${editingIndex}-${modalOpen}`}
          formEntry={formEntry}
          onFormEntryChange={setFormEntry}
          onSave={handleSave}
          onClose={closeModal}
          waterSources={waterSources || []}
          dischargeTypes={dischargeTypes || []}
          pollutants={pollutants || []}
          editing={editingIndex !== null}
        />
      </Dialog>
    </div>
  );
};
